import { RecommendationProductWithAnalytics } from '../../store/recommendationProducts/types';
import {
  TAnaliticCartProps,
  TAnaliticCartViewedProps,
  TAnaliticCartSubmitClickedProps,
  TAnaliticInstallmentProps,
  TAnaliticCartCleanedProps,
  TAnaliticRecommendationShownProps,
  RemoveProductButton
} from './types';
import { sendOldmaAnalyticsEvent } from '../providers/sendOldmaAnalyticsEvent';
import {
  FetchCartFulfilled,
  CartEmptyLoaded,
  SaleButtonClicked,
  GoToMainButtonClicked,
  FavouriteButtonClicked,
  PromocodeAppliedSuccess,
  PromocodeAppliedFailed,
  SubmitButtonClicked,
  InstallmentInformerToggle,
  CartEmptiedSuccessfully,
  RecommendationProductBlockVisible,
  OnAfterProductDecrease,
  OnAfterProductInputQuantity,
  OnAfterProductAdd,
  OnAfterProductIncrease,
  OnAfterProductCancelDeletion,
  OnAfterProductMarkForDeletion,
  OnAfterRecommendationProductAdd
} from '../dispatchers';
import { ProductWithAnalytics } from '../../store/products/types';
import { storeName as productsStore } from '../../store/products/consts';
import { storeName as summaryStore } from '../../store/summary/consts';
import { RootState } from '../../store';

const getPrescriptionType = (
  product:
    | ProductWithAnalytics
    | RecommendationProductWithAnalytics
): string => {
  let prescriptionType = 'no_recipe';

  if (product.isPku) {
    prescriptionType = 'PKU';
  } else if (product.hasAlcohol) {
    prescriptionType = 'alcohol_containing';
  } else if (product.recipeOnly) {
    prescriptionType = 'RX';
  } else if (
    product.recipeOnly &&
    'eRecipeNumber' in product &&
    product.eRecipeNumber
  ) {
    prescriptionType = 'RX_is_recipe';
  }

  return prescriptionType;
};

const bnplBlockShowEvent = (
  isCartInstallmentAvailable: boolean
) => {
  const bnplAvailability = isCartInstallmentAvailable
    ? 'available'
    : 'not_available';

  sendOldmaAnalyticsEvent({
    id: 'd-v1-e153',
    name: 'Bnpl Block Shown',
    customParams: {
      bnpl_availability: bnplAvailability,
      page: 'cart'
    }
  });
};

const collectCartViewEvent = (
  data: TAnaliticCartProps,
  cartUpdated: boolean
) => {
  const {
    quantity,
    summary,
    discount,
    notification,
    isOnlineRecipeClient,
    isActiveOnlineRecipeClient
  } = data.summary;

  const customParams: TAnaliticCartViewedProps = {
    item_id: [],
    item_price: [],
    item_name: [],
    item_prescription_type: [],
    item_delivery_type: [],
    item_is_in_stock: [],
    quantity,
    revenue: summary.value,
    discount: discount.value,
    item_is_online_recipe: [],
    delivery_info: notification,
    item_is_vmt: [],
    item_is_stm: [],
    item_is_isg: [],
    recipe_source: [],
    item_brand: [],
    item_category: [],
    item_quantity: [],
    promo_discount: [],
    is_online_recipe_client: isOnlineRecipeClient,
    is_active_online_recipe_client:
      isActiveOnlineRecipeClient,
    cart_updated: cartUpdated,
    item_is_preorder: [],
    estimated_days_to_pickup: []
  };

  if (data?.products) {
    Object.values(data.products).filter(
      (item: ProductWithAnalytics) => {
        const prescriptionType = getPrescriptionType(item);

        customParams.item_id.push(item.id);
        customParams.item_price.push(item.price.value);
        customParams.item_name.push(item.name);
        customParams.item_prescription_type.push(
          prescriptionType
        );
        customParams.item_delivery_type.push(
          item?.delivery?.allowed
            ? 'Regular delivery'
            : 'Pick up'
        );
        customParams.item_is_in_stock.push(
          item.availableCount > 0
        );
        customParams.item_is_online_recipe.push(
          !!item.eRecipeNumber
        );
        customParams.item_is_isg?.push(item.isExpired);
        customParams.recipe_source?.push(item.recipeSource);
        customParams.item_quantity?.push(item.quantity);
        customParams.item_is_vmt?.push(item.isVmt);
        customParams.item_is_stm?.push(item.isStm);
        customParams.item_brand?.push(item.vendor?.name);
        customParams.promo_discount?.push(item.discount);
        customParams.item_category?.push(item.category);
        customParams.item_is_preorder.push(
          !!item.preorderDate
        );
        customParams.estimated_days_to_pickup?.push(
          item.daysToPickup
        );
      }
    );
  }

  sendOldmaAnalyticsEvent({
    id: 'd-v1-e6',
    name: 'Cart Viewed',
    customParams
  });
};

const cartViewedEvent = (data: TAnaliticCartProps) => {
  collectCartViewEvent(data, true);
};

const cartViewedEventFirst = (data: TAnaliticCartProps) => {
  const { isCartInstallmentAvailable } = data.summary;

  collectCartViewEvent(data, false);

  bnplBlockShowEvent(isCartInstallmentAvailable);

  FetchCartFulfilled.unsubscribe(cartViewedEventFirst);
  FetchCartFulfilled.subscribe(cartViewedEvent);
};

const cartSubmitClickedEvent = (
  data: TAnaliticCartProps
) => {
  const {
    quantity,
    productSummary,
    discount,
    notification,
    isCartInstallmentAvailable
  } = data.summary;

  const customParams: TAnaliticCartSubmitClickedProps = {
    item_id: [],
    item_price: [],
    item_name: [],
    item_prescription_type: [],
    item_delivery_type: [],
    item_is_in_stock: [],
    quantity,
    revenue: productSummary.value,
    discount: discount.value,
    item_is_online_recipe: [],
    delivery_info: notification,
    item_is_vmt: [],
    item_is_stm: [],
    item_is_isg: [],
    bnpl_availability: isCartInstallmentAvailable
      ? 'available'
      : 'not_available',
    item_is_preorder: [],
    estimated_days_to_pickup: []
  };

  if (data?.products) {
    Object.values(data.products).filter(
      (item: ProductWithAnalytics) => {
        const prescriptionType = getPrescriptionType(item);

        customParams.item_id.push(item.id);
        customParams.item_price.push(item.price?.value);
        customParams.item_name.push(item.name);
        customParams.item_delivery_type.push(
          item?.delivery?.allowed
            ? 'Regular delivery'
            : 'Pick up'
        );
        customParams.item_prescription_type.push(
          prescriptionType
        );
        customParams.item_is_in_stock.push(
          item.availableCount > 0
        );
        customParams.item_is_online_recipe.push(
          !!item.eRecipeNumber
        );
        customParams.item_is_isg?.push(item.isExpired);
        customParams.item_is_vmt?.push(item.isVmt);
        customParams.item_is_stm?.push(item.isStm);
        customParams.item_is_preorder.push(
          !!item.preorderDate
        );
        customParams.estimated_days_to_pickup?.push(
          item.daysToPickup
        );
      }
    );
  }

  sendOldmaAnalyticsEvent({
    id: 'd-v1-e87',
    name: 'Cart Submit Clicked',
    customParams
  });
};

FetchCartFulfilled.subscribe(cartViewedEventFirst);

SubmitButtonClicked.subscribe(cartSubmitClickedEvent);

CartEmptyLoaded.subscribe(() => {
  sendOldmaAnalyticsEvent({
    id: 'd-v1-e95',
    name: 'Cart Empty'
  });
});

SaleButtonClicked.subscribe(() => {
  sendOldmaAnalyticsEvent({
    id: 'd-v1-e120',
    name: 'Cart Empty Sale Clicked'
  });
});

FavouriteButtonClicked.subscribe(() => {
  sendOldmaAnalyticsEvent({
    id: 'd-v1-e97',
    name: 'Cart Empty Favourite Clicked'
  });
});

GoToMainButtonClicked.subscribe(() => {
  sendOldmaAnalyticsEvent({
    id: 'd-v1-e96',
    name: 'Cart Empty Search Clicked'
  });
});

PromocodeAppliedSuccess.subscribe((data) => {
  sendOldmaAnalyticsEvent({
    id: 'd-v1-e7',
    name: 'Promocode Added',
    customParams: {
      promocode_name: data.value,
      promocode_status: 'Успешно',
      promocode_refferal: 'Basket'
    }
  });
});

PromocodeAppliedFailed.subscribe((data) => {
  sendOldmaAnalyticsEvent({
    id: 'd-v1-e7',
    name: 'Promocode Added',
    customParams: {
      promocode_name: data.value,
      promocode_status: 'Не применён',
      promocode_refferal: 'Basket'
    }
  });
});

InstallmentInformerToggle.subscribe(
  (data: TAnaliticCartProps) => {
    const {
      products,
      summary,
      isCartInstallmentAvailable
    } = data;

    if (isCartInstallmentAvailable && products && summary) {
      const { productSummary } = summary;

      const customParams: TAnaliticInstallmentProps = {
        id: [],
        item_name: [],
        item_price: [],
        cart_price: productSummary?.value || 0,
        page: 'cart'
      };

      if (products) {
        Object.values(products).filter(
          (item: ProductWithAnalytics) => {
            customParams.id.push(item.id);
            customParams.item_price.push(item.price?.value);
            customParams.item_name.push(item.name);
          }
        );
      }

      sendOldmaAnalyticsEvent({
        id: 'd-v1-e154',
        name: 'Bnpl Block Clicked',
        customParams
      });
    }
  }
);

CartEmptiedSuccessfully.subscribe((state: RootState) => {
  const products = state[productsStore].entities;
  const { quantity, productSummary, discount } =
    state[summaryStore].entity;

  const customParams: TAnaliticCartCleanedProps = {
    item_id: [],
    item_price: [],
    item_name: [],
    item_prescription_type: [],
    item_delivery_type: [],
    item_is_in_stock: [],
    quantity,
    revenue: productSummary.value,
    discount: discount.value,
    item_is_online_recipe: [],
    item_is_preorder: [],
    estimated_days_to_pickup: []
  };

  if (products) {
    Object.values(products).filter(
      (item: ProductWithAnalytics) => {
        const prescriptionType = getPrescriptionType(item);

        customParams.item_id.push(item.id);
        customParams.item_price.push(item.price?.value);
        customParams.item_name.push(item.name);
        customParams.item_prescription_type.push(
          prescriptionType
        );
        customParams.item_delivery_type.push(
          item?.delivery?.allowed
            ? 'Regular delivery'
            : 'Pick up'
        );
        customParams.item_is_in_stock.push(
          item.availableCount > 0
        );
        customParams.item_is_online_recipe.push(
          !!item.eRecipeNumber
        );
        customParams.item_is_preorder.push(
          !!item.preorderDate
        );
        customParams.estimated_days_to_pickup?.push(
          item.daysToPickup
        );
      }
    );
  }

  sendOldmaAnalyticsEvent({
    id: 'd-v1-e500',
    name: 'Cart Cleaned',
    customParams
  });
});

RecommendationProductBlockVisible.subscribe(
  (data: TAnaliticCartProps) => {
    const customParams: TAnaliticRecommendationShownProps =
      {
        block_type: 'Might be useful',
        item_id: [],
        item_price: [],
        item_rating: []
      };

    if (data.products) {
      Object.values(data.products).filter(
        (item: ProductWithAnalytics) => {
          customParams.item_id?.push(item.id);
          customParams.item_price?.push(item.price?.value);
        }
      );
    }

    sendOldmaAnalyticsEvent({
      id: 'd-v1-e84',
      name: 'Cart Block Shown',
      customParams
    });
  }
);

const addRecommendationProduct = (
  product: RecommendationProductWithAnalytics
) => {
  sendOldmaAnalyticsEvent({
    id: 'd-v1-e3',
    name: 'Item Added to Cart',
    customParams: {
      item_id: product.id,
      item_price: product.price?.value,
      item_name: product.name,
      item_prescription_type: getPrescriptionType(product),
      item_delivery_type: '',
      item_is_in_stock: true,
      item_added_from: 'Cart block useful',
      item_serp_position: '',
      item_suggest_position: '',
      search_query: '',
      item_button_type: 'button',
      item_is_online_recipe: '',
      item_tooltip_quantity: false,
      parent_analogues_is_in_stock: '',
      item_is_vmt: product.isVmt,
      item_is_stm: product.isStm,
      item_brand: '',
      item_quantity: '',
      item_category: '',
      item_is_isg: '',
      item_is_preorder: !!product.isPreorder,
      estimated_days_to_pickup: ''
    }
  });
};

const addProduct = (product: ProductWithAnalytics) => {
  sendOldmaAnalyticsEvent({
    id: 'd-v1-e3',
    name: 'Item Added to Cart',
    customParams: {
      item_id: product.id,
      item_price: product.price?.value,
      item_name: product.name,
      item_prescription_type: getPrescriptionType(product),
      item_delivery_type: product.delivery?.allowed
        ? 'Regular delivery'
        : 'Pick up',
      item_is_in_stock: product.availableCount > 0,
      item_added_from: 'cart_main',
      item_serp_position: '',
      item_suggest_position: '',
      search_query: '',
      item_button_type: 'plus',
      item_is_online_recipe: !!product.eRecipeNumber,
      item_tooltip_quantity:
        product.quantity === product.availableCount,
      parent_analogues_is_in_stock: '',
      item_is_vmt: product.isVmt,
      item_is_stm: product.isStm,
      item_brand: product.vendor?.name,
      item_quantity: product.quantity,
      item_category: product.category,
      item_is_isg: product.isExpired,
      item_is_preorder: !!product.preorderDate,
      estimated_days_to_pickup: product.daysToPickup
    }
  });
};

const removeProduct = (
  product: ProductWithAnalytics,
  buttonType: RemoveProductButton
) => {
  sendOldmaAnalyticsEvent({
    id: 'd-v1-e4',
    name: 'Item Removed from Cart',
    customParams: {
      item_id: product.id,
      item_price: product.price?.value,
      item_name: product.name,
      item_prescription_type: getPrescriptionType(product),
      item_delivery_type: product.delivery?.allowed
        ? 'Regular delivery'
        : 'Pick up',
      item_is_in_stock: product.availableCount > 0,
      item_removed_from: 'cart_main',
      item_button_type: buttonType,
      item_is_online_recipe: !!product.eRecipeNumber,
      item_quantity: product.quantity,
      item_is_vmt: product.isVmt,
      recipe_source: product.recipeSource,
      item_is_preorder: !!product.preorderDate,
      estimated_days_to_pickup: product.daysToPickup
    }
  });
};

const changeQuantityProduct = (
  product: ProductWithAnalytics,
  buttonType: RemoveProductButton
) => {
  switch (buttonType) {
    case 'manual':
      if (product.quantity === 0) {
        return removeProduct(
          product,
          RemoveProductButton.manual
        );
      }

      return addProduct(product);
    case 'minus':
      return removeProduct(
        product,
        RemoveProductButton.minus
      );
  }
};

const cancelDeletionProduct = (
  product: ProductWithAnalytics
) => {
  sendOldmaAnalyticsEvent({
    id: 'd-v1-e88',
    name: 'Item Returned',
    customParams: {
      item_id: product.id,
      item_price: product.price?.value,
      item_name: product.name,
      item_prescription_type: getPrescriptionType(product),
      item_is_preorder: !!product.preorderDate,
      estimated_days_to_pickup: product.daysToPickup
    }
  });

  sendOldmaAnalyticsEvent({
    id: 'd-v1-e3',
    name: 'Item Added to Cart',
    customParams: {
      item_id: product.id,
      item_price: product.price?.value,
      item_name: product.name,
      item_prescription_type: getPrescriptionType(product),
      item_delivery_type: product.delivery?.allowed
        ? 'Regular delivery'
        : 'Pick up',
      item_is_in_stock: product.availableCount > 0,
      item_added_from: 'cart_main',
      item_serp_position: '',
      item_suggest_position: '',
      search_query: '',
      item_button_type: 'button',
      item_is_online_recipe: !!product.eRecipeNumber,
      item_tooltip_quantity:
        product.quantity === product.availableCount,
      parent_analogues_is_in_stock: '',
      item_is_vmt: product.isVmt,
      item_is_stm: product.isStm,
      item_brand: product.vendor?.name,
      item_quantity: product.quantity,
      item_category: product.category,
      item_is_isg: product.isExpired,
      item_is_preorder: !!product.preorderDate,
      estimated_days_to_pickup: product.daysToPickup
    }
  });
};

OnAfterProductAdd.subscribe((product) =>
  addProduct(product)
);
OnAfterProductIncrease.subscribe((product) =>
  addProduct(product)
);
OnAfterProductDecrease.subscribe((product) =>
  changeQuantityProduct(product, RemoveProductButton.minus)
);
OnAfterProductInputQuantity.subscribe((product) =>
  changeQuantityProduct(product, RemoveProductButton.manual)
);
OnAfterProductCancelDeletion.subscribe((product) =>
  cancelDeletionProduct(product)
);
OnAfterProductMarkForDeletion.subscribe((product) =>
  removeProduct(
    product,
    RemoveProductButton.cartClearButton
  )
);
OnAfterRecommendationProductAdd.subscribe((product) =>
  addRecommendationProduct(product)
);
